import "~/app/thirdParty/boot";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/en";
import { initializeComponentRegistry, mountStorefrontApp } from "~/utils/componentRegistry";
import { registerOccTransport } from "~/app/api/occ/registerOccTransport";
import { registerActiveCartPort } from "~/app/cart/registerActiveCartPort";
import { registerConfiguratorResumeStore } from "~/app/configurator/configuratorResumeStore";
import "~/styles/style_vendor.less";
import { setupStore } from "~/app/store";
import { initializeCurrentCartContentsTracking } from "~/features/cart/cart/utils/trackCurrentCartContents";
import { getActiveCartData } from "~/features/cart/cart/api/CartView";
import { startPunchoutContinuationFromLocation } from "~/features/cart/cart/Punchout/punchoutContinuation";
import { initializeConfiguratorCatalog } from "~/bridges/configurator/configuratorCatalog";
import { initializePageInitialDataLoader } from "~/app/api/pageData";
import { initializeAccountContextTransition } from "~/app/accountContextTransition";
import { initializeLogout } from "~/features/auth/api/doLogout";
import { installCrossTabAuthReceiver } from "~/features/auth/api/crossTabAuthReceiver";
import { initializeSessionExpiration, triggerSessionExpired } from "~/app/api/triggerSessionExpired";
import { setSessionExpiredListener } from "~/app/api/occ/sessionExpiredSignal";
import { createInitialHashScroller } from "~/common/createInitialHashScroller";
import { bindLmsLinkListener } from "~/features/lms/LmsLinkListener";
import { initZoovuTracking } from "~/app/analytics/zoovu/initZoovuTracking";
import "~/app/analytics";

/**
 * Transitional legacy runtime seam (plan §5.3, §7.2): the Legacy Host consumes
 * `@schaeffler-commerce/core/legacy-runtime` for legacy runtime initialization
 * and the single-root storefront mount (task 0224). This is a migration seam,
 * not a future headless contract — the headless host composes
 * `StorefrontAppTree` itself.
 */
export { mountStorefrontApp };

export function initializeLegacyRuntime() {
  dayjs.extend(utc);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(localizedFormat);
  dayjs.locale({ ...dayjs.Ls.en, weekStart: 1 });

  registerOccTransport();
  initializeConfiguratorCatalog();
  const store = setupStore();
  initializeAccountContextTransition(store);
  initializeLogout(store.dispatch);
  initializeSessionExpiration(store);
  setSessionExpiredListener(triggerSessionExpired);
  // 0213: cross-tab auth receiver — login/logout/expiry notifications from
  // the origin's other tabs drop this tab's identity-bound projections and
  // re-resolve through the session query. No-op on the SSR server (the
  // channel module guards itself).
  installCrossTabAuthReceiver();
  initializePageInitialDataLoader(store.dispatch);
  initializeCurrentCartContentsTracking({
    loadCartLineItems: async () => Object.values((await getActiveCartData())?.packageEntriesById ?? {}),
    getProductsByCode: () => store.getState().products.basic,
  });
  registerActiveCartPort();
  registerConfiguratorResumeStore();
  initializeComponentRegistry(store);
  // cXML punchout continuation start (0253 lane E1): reads location and
  // sessionStorage, fires occFetch (registerOccTransport ran above); the
  // restore phase completes before any render mounts SessionProvider.
  startPunchoutContinuationFromLocation();
  return store;
}

/** Post-ready DOM/listener boot steps; the Legacy Host calls this after mountStorefrontApp(). */
export function finalizeLegacyBoot() {
  window.addEventListener("load", () => {
    void initZoovuTracking();
  });

  createInitialHashScroller();

  bindLmsLinkListener();
}
